import type { IntakePrefill } from '@lixionary-jira/domain';
import {
  TYPE_LABELS,
  type AssistantActivity,
  type AssistantQuestionPrompt,
  type TicketDraft,
} from '@lixionary-jira/domain';
import { useQuery } from '@tanstack/react-query';
import { Bot, Send, Sparkles } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import {
  isBusy,
  useAssistantSession,
  type AssistantSession,
  type AssistantSessionSummary,
} from '../../api/assistant';
import type { TicketCreation } from '../../api/creation';
import { useAction } from '../../api/hooks';
import { Button } from '../../ui/Button';
import { Alert, ErrorAlert, Spinner } from '../../ui/feedback';
import { Select, Textarea } from '../../ui/forms';
import { Markdown } from '../../ui/Markdown';
import { Avatar, Card } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';
import { QuestionForm, QuestionList } from './AssistantQuestions';

const EXAMPLES = [
  'Visit notes cannot be exported as PDF; only CSV works.',
  'The board should let me filter by label and remember it.',
];

/**
 * Create a ticket through a conversation. The assistant reads the project's repository, asks clarifying
 * questions, then proposes a complete draft the requester can submit or refine in the form.
 */
export function AssistantDraft({
  creation,
  sessionId,
  onSession,
  onEdit,
  onSubmitted,
  submitTicket,
  requestContext,
  model,
}: {
  creation: TicketCreation;
  sessionId: string | null;
  onSession: (id: string | null) => void;
  onEdit: (draft: TicketDraft, sessionId: string) => void;
  onSubmitted: (ticket: { key: string; number: number }) => void;
  model: string;
  submitTicket?: (draft: TicketDraft, conversation: string) => Promise<{ key: string; number: number }>;
  requestContext?: IntakePrefill;
}) {
  const session = useAssistantSession(creation, sessionId);
  const { client, base, scope } = creation;
  const s = session.data;
  const busy = isBusy(s?.status);
  const [message, setMessage] = useState('');
  /** The question message the requester chose to answer in their own words instead of with the form. */
  const [freeTextFor, setFreeTextFor] = useState<string | null>(null);
  const logRef = useRef<HTMLDivElement>(null);

  const earlier = useQuery({
    queryKey: ['assistant-sessions', scope],
    queryFn: () => client.get<{ items: AssistantSessionSummary[] }>(`${base}/assistant/sessions`),
  });

  const send = useAction(
    async (content: string) =>
      sessionId
        ? client.post<AssistantSession>(`${base}/assistant/sessions/${sessionId}/messages`, {
            content,
            context: requestContext,
          })
        : client.post<AssistantSession>(`${base}/assistant/sessions`, { content, context: requestContext }),
    {
      invalidate: [['assistant-sessions', scope]],
      onSuccess: (r) => {
        setMessage('');
        if (r.id !== sessionId) onSession(r.id);
        void session.refetch();
      },
    },
  );
  const retry = useAction(
    () => client.post<AssistantSession>(`${base}/assistant/sessions/${sessionId}/retry`),
    { onSuccess: () => void session.refetch() },
  );
  const submit = useAction(
    (draft: TicketDraft) =>
      submitTicket
        ? submitTicket(draft, sessionId!)
        : client.post<{ key: string; number: number }>(
            `${base}/assistant/sessions/${sessionId}/submit`,
            draft,
          ),
    {
      ...(!submitTicket ? { success: (r: { key: string }) => `Submitted ${r.key}` } : {}),
      invalidate: [
        ['board', scope],
        ['dashboard', scope],
        ['assistant-sessions', scope],
      ],
      onSuccess: onSubmitted,
    },
  );

  const messageCount = s?.messages.length ?? 0;
  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight, behavior: 'smooth' });
  }, [messageCount, busy]);

  const canSend = !busy && s?.status !== 'submitted' && message.trim().length > 0 && !send.isPending;
  const sendNow = () => canSend && send.mutate(message.trim());
  const resumable = (earlier.data?.items ?? []).filter((i) => i.status !== 'submitted' && i.id !== sessionId);
  // Questions from the latest assistant turn wait for answers; they are answered through the form.
  const last = s?.messages.at(-1);
  const pending = last?.role === 'assistant' && last.prompt && s?.status === 'idle' ? last : null;
  const questionForm = pending?.prompt && freeTextFor !== pending.id ? pending.prompt : null;

  return (
    <div className="stack lg">
      <Card
        title={
          <span className="row nowrap">
            <Sparkles size={18} aria-hidden /> Ticket assistant
          </span>
        }
        subtitle={`Explores the project's code with ${model} to verify your request, asks what it cannot find, then drafts the ticket. It never changes code.`}
        action={
          sessionId ? (
            <Button variant="ghost" size="sm" onClick={() => onSession(null)}>
              New conversation
            </Button>
          ) : undefined
        }
      >
        {!sessionId && resumable.length > 0 && (
          <label className="row small muted">
            Continue an earlier conversation:
            <Select
              value=""
              onChange={(e) => e.target.value && onSession(e.target.value)}
              aria-label="Earlier conversations"
            >
              <option value="">Choose…</option>
              {resumable.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.displayId} · {i.title.slice(0, 70)}
                </option>
              ))}
            </Select>
          </label>
        )}
        <div className="chat-log" ref={logRef} role="log" aria-live="polite" aria-label="Conversation">
          {!s && !send.isPending && (
            <div className="chat-empty">
              <Bot size={28} aria-hidden />
              <p>
                Describe the problem or the feature in your own words. Mention the screen, what you expected
                and what happened.
              </p>
              <div className="row">
                {EXAMPLES.map((e) => (
                  <button key={e} type="button" className="chip chip-button" onClick={() => setMessage(e)}>
                    {e}
                  </button>
                ))}
              </div>
            </div>
          )}
          {s?.messages.map((m) => (
            <ChatMessage
              requesterName={creation.requesterName}
              key={m.id}
              role={m.role}
              content={m.content}
              prompt={m.prompt}
              asking={m.id === pending?.id && !!questionForm}
              activity={m.activity}
              at={m.createdAt}
            />
          ))}
          {(busy || send.isPending) && <Working activity={s?.activity ?? []} />}
        </div>
        {s?.status === 'failed' && (
          <Alert tone="error" title="The assistant could not answer">
            <span>{s.error}</span>
            <div>
              <Button
                size="sm"
                variant="secondary"
                loading={retry.isPending}
                onClick={() => retry.mutate(undefined)}
              >
                Retry
              </Button>
            </div>
          </Alert>
        )}
        <ErrorAlert error={send.error ?? retry.error} />
        {s?.status === 'submitted' && s.ticket ? (
          <Alert tone="success" title="Ticket created">
            {creation.ticketHref ? (
              <Link to={creation.ticketHref(s.ticket.number)}>Open {s.ticket.key}</Link>
            ) : (
              <span>{s.ticket.key}</span>
            )}
          </Alert>
        ) : questionForm ? (
          <QuestionForm
            key={pending!.id}
            prompt={questionForm}
            sending={send.isPending}
            disabled={busy || send.isPending}
            onSend={(content) => send.mutate(content)}
            onFreeText={() => setFreeTextFor(pending!.id)}
          />
        ) : (
          <form
            className="chat-composer"
            onSubmit={(e) => {
              e.preventDefault();
              sendNow();
            }}
          >
            <Textarea
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  sendNow();
                }
              }}
              maxLength={8000}
              placeholder={s ? 'Answer the questions or ask for changes to the draft…' : 'What do you need?'}
              aria-label="Message to the assistant"
              disabled={busy}
            />
            <div className="row between">
              {pending ? (
                <Button variant="ghost" size="sm" onClick={() => setFreeTextFor(null)}>
                  Back to the questions
                </Button>
              ) : (
                <span className="small muted">Enter to send · Shift+Enter for a new line</span>
              )}
              <Button
                type="submit"
                size="sm"
                icon={<Send size={14} />}
                loading={send.isPending}
                disabled={!canSend}
              >
                Send
              </Button>
            </div>
          </form>
        )}
      </Card>
      {s?.draft && s.status !== 'submitted' && (
        <DraftPreview
          session={s}
          ticketHref={creation.ticketHref}
          draft={s.draft}
          busy={busy}
          submitting={submit.isPending}
          onSubmit={() => submit.mutate(s.draft!)}
          onEdit={() => onEdit(s.draft!, s.id)}
          error={submit.error}
        />
      )}
    </div>
  );
}

function ChatMessage({
  requesterName,
  role,
  content,
  prompt,
  asking,
  activity,
  at,
}: {
  requesterName: string;
  role: 'user' | 'assistant';
  content: string;
  prompt: AssistantQuestionPrompt | null;
  /** The questions are shown in the answer form below the conversation. */
  asking: boolean;
  activity: AssistantActivity[];
  at: string;
}) {
  const tools = activity.filter((a) => a.kind === 'tool');
  return (
    <div className={`chat-msg ${role}`}>
      <Avatar
        name={role === 'user' ? requesterName : 'Assistant'}
        kind={role === 'user' ? 'human' : 'ai_service'}
        small
      />
      <div className="chat-bubble">
        {role === 'user' ? (
          <p className="chat-text">{content}</p>
        ) : prompt ? (
          <>
            <Markdown>{prompt.message}</Markdown>
            {asking ? (
              <span className="small muted">Answer the questions below.</span>
            ) : (
              <QuestionList prompt={prompt} />
            )}
          </>
        ) : (
          <Markdown>{content}</Markdown>
        )}
        {tools.length > 0 && (
          <details className="chat-activity">
            <summary>
              Checked the code in {tools.length} step{tools.length === 1 ? '' : 's'}
            </summary>
            <ActivityList activity={activity} />
          </details>
        )}
        <span className="small muted">
          <Timestamp value={at} />
        </span>
      </div>
    </div>
  );
}

function Working({ activity }: { activity: AssistantActivity[] }) {
  const recent = activity.slice(-6);
  return (
    <div className="chat-msg assistant">
      <Avatar name="Assistant" kind="ai_service" small />
      <div className="chat-bubble working">
        <span className="row nowrap small">
          <Spinner /> {activity.length ? 'Exploring the code…' : 'Thinking…'}
        </span>
        {recent.length > 0 && <ActivityList activity={recent} />}
      </div>
    </div>
  );
}

export function ActivityList({ activity }: { activity: AssistantActivity[] }) {
  return (
    <ul className="activity-list">
      {activity.map((a, i) => (
        <li key={`${a.at}-${i}`} className={a.kind === 'note' ? 'muted' : undefined} title={a.detail}>
          {a.label}
        </li>
      ))}
    </ul>
  );
}

function DraftPreview({
  session,
  ticketHref,
  draft,
  busy,
  submitting,
  onSubmit,
  onEdit,
  error,
}: {
  session: AssistantSession;
  ticketHref: TicketCreation['ticketHref'];
  draft: TicketDraft;
  busy: boolean;
  submitting: boolean;
  onSubmit: () => void;
  onEdit: () => void;
  error: unknown;
}) {
  const a = session.analysis;
  return (
    <Card
      title="Proposed ticket"
      subtitle={`${TYPE_LABELS[draft.type]} · drafted in ${session.displayId}${session.repositoryRevision ? ` against commit ${session.repositoryRevision.slice(0, 10)}` : ''}`}
    >
      <h3 className="draft-title">{draft.title}</h3>
      {draft.labels.length > 0 && (
        <div className="row">
          {draft.labels.map((l) => (
            <span key={l} className="chip">
              {l}
            </span>
          ))}
        </div>
      )}
      <div className="well">
        <Markdown>{draft.description}</Markdown>
      </div>
      {draft.stepsOrOutcome && (
        <div className="stack sm">
          <strong className="small">
            {draft.type === 'issue' ? 'Reproduction steps' : 'Intended outcome'}
          </strong>
          <Markdown>{draft.stepsOrOutcome}</Markdown>
        </div>
      )}
      {draft.acceptanceCriteria.length > 0 && (
        <div className="stack sm">
          <strong className="small">Acceptance criteria</strong>
          <ol className="criteria-list">
            {draft.acceptanceCriteria.map((c, i) => (
              <li key={i}>
                <Markdown>{c}</Markdown>
              </li>
            ))}
          </ol>
        </div>
      )}
      {a && <AnalysisDetails analysis={a} ticketHref={ticketHref} />}
      <ErrorAlert error={error} />
      <div className="form-actions inline">
        <Button variant="secondary" onClick={onEdit} disabled={busy}>
          Edit in form
        </Button>
        <Button onClick={onSubmit} loading={submitting} disabled={busy}>
          Submit request
        </Button>
      </div>
    </Card>
  );
}

export function AnalysisDetails({
  analysis,
  ticketHref,
}: {
  analysis: {
    summary?: string;
    openQuestions?: string[];
    affectedAreas?: { path: string; reason: string }[];
    possibleDuplicates?: string[];
  };
  /** Links related tickets; omitted, keys are shown as plain text. */
  ticketHref?: ((number: number) => string) | undefined;
}) {
  return (
    <div className="stack sm">
      {analysis.summary && <Markdown>{analysis.summary}</Markdown>}
      {!!analysis.affectedAreas?.length && (
        <div className="stack sm">
          <strong className="small">Code involved</strong>
          <ul className="code-refs">
            {analysis.affectedAreas.map((r) => (
              <li key={r.path}>
                <span className="mono">{r.path}</span>
                {r.reason && <span className="muted"> — {r.reason}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {!!analysis.openQuestions?.length && (
        <div className="stack sm">
          <strong className="small">Still open</strong>
          <ul>
            {analysis.openQuestions.map((q) => (
              <li key={q}>{q}</li>
            ))}
          </ul>
        </div>
      )}
      {!!analysis.possibleDuplicates?.length && (
        <p className="small">
          Possibly related:{' '}
          {analysis.possibleDuplicates.map((k, i) => {
            const n = /(\d+)$/.exec(k)?.[1];
            return (
              <span key={k}>
                {i > 0 && ', '}
                {n && ticketHref ? <Link to={ticketHref(Number(n))}>{k}</Link> : k}
              </span>
            );
          })}
        </p>
      )}
    </div>
  );
}
